import type { Compare } from "@suse-o11y/compare";
import { isStrictEqual } from "@suse-o11y/is-strict-equal";
import { max } from "@suse-o11y/max";
import { onTimeout } from "@suse-o11y/on-timeout";
import { useEffect, useRef, useState } from "react";

export interface UseDelayedOptions<T> {
	fallback?: T;
	/**
	 * @default isStrictEqual
	 */
	compare?: Compare<T>;
}

/**
 * A custom hook that returns a value after a specified delay.
 *
 * @template T - The type of the value.
 * @param {T} value - The value to be delayed.
 * @param {number} delay - The delay in milliseconds.
 * @param {UseDelayedOptions<T>} [options] - Optional configuration for the hook.
 * @param {T} [options.fallback=value] - The fallback value to use initially.
 * @param {(a: T, b: T) => boolean} [options.compare=isStrictEqual] - A function to compare the previous and current values.
 * @returns {T} - The delayed value.
 */
export function useDelayed<T>(
	value: T,
	delay: number,
	{ fallback = value, compare = isStrictEqual }: UseDelayedOptions<T> = {},
): T {
	const normalizedDelay = max(0, delay);
	const [delayedValue, setDelayedValue] = useState(fallback);

	const compareRef = useRef(compare);

	useEffect(() => {
		compareRef.current = compare;
	}, [compare]);

	useEffect(
		() =>
			onTimeout(() => {
				setDelayedValue((prev) => (compareRef.current(prev, value) ? prev : value));
			}, normalizedDelay),
		[value, normalizedDelay],
	);

	return delayedValue;
}
